import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Upload, Move, LockKeyhole, Navigation, ArrowRight, ArrowUpRight, MapPin, Layers, Ruler, Satellite } from "lucide-react";

const STEPS = [
  { n: "01", t: "UPLOAD", d: "Drop in your site plan, grading sheet or scanned blueprint. PDF, JPG or PNG.", Icon: Upload },
  { n: "02", t: "ALIGN", d: "Drag, pinch and rotate the plan over live satellite imagery until it lines up.", Icon: Move },
  { n: "03", t: "LOCK", d: "Lock the overlay geographically once real-world features match.", Icon: LockKeyhole },
  { n: "04", t: "GO", d: "Get in the machine. Watch your live position move across the plan.", Icon: Navigation },
];

const EQUIP = ["Excavators", "Dozers", "Scrapers", "Loaders", "Haul Trucks"];

const FEATURES = [
  { Icon: Satellite, t: "Live Satellite Base", d: "Clean aerial imagery under every plan — compare the design to the actual ground." },
  { Icon: Layers, t: "Adjustable Opacity", d: "Slide the blueprint to ~50% and line up roads, ponds, pads and building corners by eye." },
  { Icon: MapPin, t: "Live GPS Position", d: "A high-visibility YOU-ARE-HERE marker moves with you across the engineered plan." },
  { Icon: Ruler, t: "Field Measure Tools", d: "Drop points, measure distance and area, and check how far you are from a feature." },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-void">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1517089258673-4b72e709a9c5?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Aerial construction site" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/70 to-void" />
        <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-20 lg:px-8 lg:pb-32 lg:pt-28">
          <span className="inline-block border border-edge bg-panel/60 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-widest text-yellow">
            Construction Field Mapping
          </span>
          <h1 className="mt-6 max-w-4xl font-head text-5xl font-black uppercase leading-[0.92] tracking-tight text-gray-100 sm:text-6xl lg:text-7xl">
            Put the plans<br />on the <span className="text-yellow">ground.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-gray-400">
            GradeTrack puts your construction plans directly over the real world — giving operators a
            live view of exactly where they are on the site.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link to="/signin" data-testid="hero-start-btn"
              className="flex items-center justify-center gap-2 bg-yellow px-8 py-4 font-head text-base font-bold uppercase tracking-wider text-black transition-colors hover:bg-yellow-hover">
              Start Free <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/app" data-testid="hero-map-btn"
              className="flex items-center justify-center gap-2 border border-edge bg-panel/60 px-8 py-4 font-head text-base font-bold uppercase tracking-wider text-gray-100 transition-colors hover:border-yellow hover:text-yellow">
              Open The Map
            </Link>
          </div>
          <p className="mt-10 font-head text-sm font-bold uppercase tracking-[0.3em] text-gray-500">
            Upload. <span className="text-gray-300">Align.</span> <span className="text-gray-100">Lock.</span> <span className="text-yellow">Go.</span>
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-edge bg-void py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col gap-3">
            <span className="font-mono text-xs font-semibold uppercase tracking-widest text-yellow">How it works</span>
            <h2 className="max-w-2xl font-head text-3xl font-extrabold uppercase leading-none tracking-tight text-gray-100 lg:text-5xl">
              Four steps from<br />sheet to seat.
            </h2>
          </div>
          <div className="mt-14 grid gap-px border border-edge bg-edge sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(({ n, t, d, Icon }) => (
              <div key={n} className="group bg-void p-8 transition-colors hover:bg-panel">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm font-bold text-gray-600">{n}</span>
                  <Icon className="h-6 w-6 text-yellow" />
                </div>
                <h3 className="mt-8 font-head text-2xl font-bold uppercase tracking-wide text-gray-100">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="dot-bg py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <span className="font-mono text-xs font-semibold uppercase tracking-widest text-yellow">A second set of eyes</span>
              <h2 className="mt-3 font-head text-3xl font-extrabold uppercase leading-none tracking-tight text-gray-100 lg:text-5xl">
                See the plan.<br />See the ground.<br /><span className="text-yellow">Know where you are.</span>
              </h2>
              <p className="mt-5 max-w-md text-gray-400">
                GradeTrack is a simple field visualization tool — not a replacement for machine control.
                It helps operators and foremen understand where they are on the plans.
              </p>
              <div className="mt-6 border-l-2 border-yellow bg-panel/50 px-5 py-4">
                <p className="font-mono text-xs uppercase leading-relaxed tracking-wide text-gray-400">
                  Standard phone GPS is approximate and is <span className="text-yellow">not survey-grade</span>.
                  Built to support external RTK/GNSS receivers in the future.
                </p>
              </div>
            </div>
            <div className="grid gap-px border border-edge bg-edge sm:grid-cols-2">
              {FEATURES.map(({ Icon, t, d }) => (
                <div key={t} className="bg-void p-7">
                  <Icon className="h-7 w-7 text-yellow" />
                  <h3 className="mt-5 font-head text-lg font-bold uppercase tracking-wide text-gray-100">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EQUIPMENT */}
      <section className="relative overflow-hidden border-y border-edge py-20">
        <img src="https://images.unsplash.com/photo-1606811883055-0d24f05c8bcd?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Excavator" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="font-head text-3xl font-extrabold uppercase tracking-tight text-gray-100 lg:text-4xl">
            Built for the cab.
          </h2>
          <p className="mt-3 max-w-lg text-gray-400">
            Mobile-first with large, glove-friendly controls. Mount your phone or tablet and keep both
            hands on the machine.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            {EQUIP.map((e) => (
              <span key={e} className="border border-edge bg-panel/70 px-5 py-3 font-head text-sm font-bold uppercase tracking-wider text-gray-200">
                {e}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CREWTRACK */}
      <section className="bg-void py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="border border-edge bg-cardbg p-8 lg:p-12" data-testid="crewtrack-ecosystem-card">
            <span className="font-mono text-xs font-semibold uppercase tracking-widest text-gray-500">More from our construction software</span>
            <div className="mt-6 grid gap-10 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <div className="font-head text-3xl font-bold uppercase tracking-tighter">
                  <span className="text-gray-100">CREW</span><span className="text-crew">TRACK</span>
                </div>
                <p className="mt-4 max-w-xl text-gray-400">
                  Our sister product. CrewTrack manages crews, equipment, time, labor, production and job
                  costing — connecting the field to the office. Same construction-first philosophy, different tools.
                </p>
                <div className="mt-5 font-mono text-xs uppercase tracking-widest text-gray-500">
                  People • Equipment • Time • Labor • Production
                </div>
              </div>
              <a href="https://crewtrack.team" target="_blank" rel="noopener noreferrer" data-testid="home-crewtrack-link"
                className="inline-flex items-center gap-2 border border-crew px-6 py-3.5 font-head text-sm font-bold uppercase tracking-wider text-crew transition-colors hover:bg-crew hover:text-black">
                Visit CrewTrack <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-edge bg-panel py-20">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="font-head text-4xl font-black uppercase leading-none tracking-tight text-gray-100 lg:text-6xl">
            Get in the machine<br />and <span className="text-yellow">go.</span>
          </h2>
          <Link to="/signin"
            className="mt-8 inline-flex items-center gap-2 bg-yellow px-10 py-4 font-head text-base font-bold uppercase tracking-wider text-black transition-colors hover:bg-yellow-hover">
            Start Free <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
